import AlertCircle from "@redotech/redo-icons/alert-circle.svg";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { BgColorValue, BorderColorValue } from "../../theme/box";
import {
  RedoButton,
  RedoButtonHierarchy,
  RedoButtonTheme,
} from "../buttons/redo-button";
import {
  FeaturedIconColor,
  RedoFeaturedIcon,
} from "../featured-icon/redo-featured-icon";

export type NotificationType = "error" | "warning" | "info" | "success";

const notificationTypeToFeaturedIconColor: Record<
  NotificationType,
  FeaturedIconColor
> = { error: "error", warning: "warning", info: "gray", success: "success" };

interface ButtonAction {
  actionType: "button";
  hierarchy: Extract<RedoButtonHierarchy, "primary" | "secondary" | "tertiary">;
  onClick: () => void;
  text: string;
  theme?: RedoButtonTheme;
  pending?: boolean;
}

interface LinkAction {
  actionType: "link";
  text: string;
  href: string;
  target?: "_blank" | "_self";
  internal: boolean;
  onClick?: () => void;
}

export type NotificationBannerAction = ButtonAction | LinkAction;

export interface NotificationBannerItem {
  id?: string;
  message: string;
  type: NotificationType;
  actions?: NotificationBannerAction[];
  bgColor?: BgColorValue;
  borderColor?: BorderColorValue;
}

interface NotificationBannerProps {
  items: NotificationBannerItem[];
  onDismiss?: (id: string) => void;
}

export const RedoNotificationBanner = memo(function Notification({
  items,
  onDismiss,
}: NotificationBannerProps) {
  const [index, setIndex] = useState(0);

  if (items.length === 0) {
    return null;
  }

  const item = items[index];
  if (!item) {
    return null;
  }
  const {
    id = `${index}`,
    type,
    message,
    actions = [],
    bgColor = "primary",
    borderColor = "primary",
  } = item;

  return (
    <Flex
      bgColor={bgColor}
      borderBottomWidth="1px"
      borderColor={borderColor}
      borderStyle="solid"
      px="3xl"
      py="lg"
      w="full"
    >
      <RedoFeaturedIcon
        color={notificationTypeToFeaturedIconColor[type]}
        Icon={AlertCircle}
      />
      <Flex align="center" dir="row" justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {message}
        </Text>
        <Flex align="center" gap="md">
          {actions.map((action, index) => {
            if (action.actionType === "button") {
              const { actionType, ...buttonProps } = action;
              return (
                <RedoButton
                  hierarchy={buttonProps.hierarchy}
                  key={index}
                  onClick={buttonProps.onClick}
                  pending={buttonProps.pending}
                  size="sm"
                  text={buttonProps.text}
                  theme={buttonProps.theme}
                />
              );
            } else if (action.actionType === "link") {
              const target = action.target || "_blank";

              if (action.internal) {
                return (
                  <Link
                    key={index}
                    onClick={action.onClick}
                    target={target}
                    to={action.href}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textColor="primary"
                      textDecoration="underline"
                    >
                      {action.text}
                    </Text>
                  </Link>
                );
              }
              return (
                <a
                  href={action.href}
                  key={index}
                  onClick={action.onClick}
                  rel={target === "_blank" ? "noreferrer" : undefined}
                  target={target}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    textColor="primary"
                    textDecoration="underline"
                  >
                    {action.text}
                  </Text>
                </a>
              );
            }
            return null;
          })}
          {items.length > 1 && (
            <Flex align="center">
              <RedoButton
                aria-label="Previous"
                IconLeading={ArrowLeftIcon}
                onClick={() =>
                  setIndex((index - 1 + items.length) % items.length)
                }
              />
              <Text
                fontSize="xxs"
                fontWeight="semibold"
                textColor="primary"
              >{`${index + 1} / ${items.length}`}</Text>
              <RedoButton
                aria-label="Next"
                IconLeading={ArrowRightIcon}
                onClick={() => setIndex((index + 1) % items.length)}
              />
            </Flex>
          )}
          {onDismiss && (
            <RedoButton
              IconLeading={CloseIcon}
              onClick={() => onDismiss(id)}
              size="sm"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
